import React, { useState } from 'react';

// Fixed viewBox; the <svg> scales responsively via CSS width:100%, same
// convention as TrendChart.
const VIEW_W = 760;
const VIEW_H = 220;
const PADDING = { top: 8, right: 16, bottom: 24, left: 40 };
const PLOT_W = VIEW_W - PADDING.left - PADDING.right;
const PLOT_H = VIEW_H - PADDING.top - PADDING.bottom;
const HOURS = 24;
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOUR_LABEL_EVERY = 3;

const cellW = PLOT_W / HOURS;
const cellH = PLOT_H / DAY_LABELS.length;

const formatHour = (hour) => {
  const period = hour < 12 ? 'AM' : 'PM';
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour} ${period}`;
};

// Fixed opacity steps applied to var(--color-primary) — index 0 (no calls)
// isn't used here, it renders as var(--color-border) instead so "no data"
// never looks like a very-quiet-but-present shade of the same hue.
const BAND_OPACITY = [null, 0.32, 0.52, 0.74, 1];

// Over a full month, nearly every hour bucket has *some* calls, so a
// continuous value/max scale compresses almost everything into one muddy
// mid-tone (a handful of outlier hours would otherwise stretch the whole
// scale). Quartile bands of the actual non-zero values — the same idea as a
// GitHub contribution graph — keep the busy hours visually distinct instead.
const buildBander = (grid) => {
  const nonZero = grid.flat().filter((value) => value > 0).sort((a, b) => a - b);
  const quantile = (p) => {
    if (nonZero.length === 0) return 0;
    return nonZero[Math.min(nonZero.length - 1, Math.floor(p * nonZero.length))];
  };
  const q25 = quantile(0.25);
  const q50 = quantile(0.5);
  const q75 = quantile(0.75);

  return (value) => {
    if (value === 0) return 0;
    if (value <= q25) return 1;
    if (value <= q50) return 2;
    if (value <= q75) return 3;
    return 4;
  };
};

// grid: 7 (day, 0=Sun) x 24 (hour) array of call counts, from
// TrendAnalytics.buildHeatmapGrid — already scoped to the page's current
// date range, same as the points passed to TrendChart.
function PeakHoursHeatmap({ grid }) {
  const [hovered, setHovered] = useState(null); // { day, hour, value } | null

  const hasActivity = grid.some((row) => row.some((value) => value > 0));

  if (!hasActivity) {
    return <p className="agent-page-empty">No call activity in this date range.</p>;
  }

  // Direct-label the extreme (busiest slot) so the headline finding reads
  // without needing to hover every cell — the legend + docked detail carry the rest.
  let busiest = { day: 0, hour: 0, value: -1 };
  grid.forEach((row, day) => {
    row.forEach((value, hour) => {
      if (value > busiest.value) busiest = { day, hour, value };
    });
  });

  const bandOf = buildBander(grid);

  // Detail shown in the header: the hovered cell while hovering, otherwise
  // the busiest slot. Docked in place instead of a floating tooltip so it
  // never overlaps the grid or gets clipped near the edges.
  const detail = hovered ?? busiest;
  const isHoverDetail = hovered !== null;

  return (
    <div className="chart-card heatmap-card">
      <div className="trend-chart-header">
        <div>
          <h3 className="heatmap-title">Peak Hours</h3>
          <p className={`heatmap-subtitle${isHoverDetail ? ' heatmap-subtitle-hover' : ''}`}>
            {isHoverDetail ? '' : 'Busiest: '}
            {DAY_LABELS[detail.day]} {formatHour(detail.hour)} · {detail.value} call
            {detail.value === 1 ? '' : 's'}
          </p>
        </div>
        <div className="heatmap-scale-legend" aria-hidden="true">
          <span>Fewer</span>
          <span className="heatmap-scale-chip" style={{ backgroundColor: 'var(--color-border)' }} />
          {BAND_OPACITY.slice(1).map((opacity, i) => (
            <span
              key={i}
              className="heatmap-scale-chip"
              style={{ backgroundColor: 'var(--color-primary)', opacity }}
            />
          ))}
          <span>More</span>
        </div>
      </div>

      <div className="heatmap-wrap">
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="heatmap-svg" role="img" aria-label="Call volume by day of week and hour of day">
          {DAY_LABELS.map((label, day) => (
            <text
              key={label}
              x={PADDING.left - 8}
              y={PADDING.top + cellH * day + cellH / 2 + 4}
              className="trend-axis-label heatmap-row-label"
              textAnchor="end"
            >
              {label}
            </text>
          ))}

          {Array.from({ length: HOURS }, (_, hour) =>
            hour % HOUR_LABEL_EVERY === 0 ? (
              <text
                key={hour}
                x={PADDING.left + cellW * hour + cellW / 2}
                y={VIEW_H - 6}
                className="trend-axis-label"
                textAnchor="middle"
              >
                {hour}:00
              </text>
            ) : null
          )}

          {grid.map((row, day) =>
            row.map((value, hour) => {
              const isBusiest = value > 0 && day === busiest.day && hour === busiest.hour;
              const isHovered = hovered !== null && day === hovered.day && hour === hovered.hour;
              const band = bandOf(value);
              return (
                <rect
                  key={`${day}-${hour}`}
                  className={`heatmap-cell${isBusiest ? ' heatmap-cell-busiest' : ''}${isHovered ? ' heatmap-cell-hovered' : ''}`}
                  x={PADDING.left + cellW * hour}
                  y={PADDING.top + cellH * day}
                  width={cellW}
                  height={cellH}
                  rx={2}
                  fill={band === 0 ? 'var(--color-border)' : 'var(--color-primary)'}
                  fillOpacity={band === 0 ? 1 : BAND_OPACITY[band]}
                  onMouseEnter={() => setHovered({ day, hour, value })}
                  onMouseLeave={() => setHovered(null)}
                />
              );
            })
          )}
        </svg>
      </div>
    </div>
  );
}

export default PeakHoursHeatmap;
